import { Card, Tabs, Typography } from "@hellocarbo/lasagne";
import { BarChart2, Users } from "lucide-react";
import { use } from "react";
import { useTranslation } from "react-i18next";
import { data, useLoaderData } from "react-router";

import OptionItem from "@/components/Option/OptionItem";
import ProServicesContent from "@/components/Option/ProServicesContent";
import PageWrapper from "@/components/ui/PageWrapper";
import { UserContext } from "@/contexts";

enum OPTIONS_VIEWS {
  byOptions = "options",
  byProServices = "services",
}

export async function loader() {
  const optionsResponse = await fetch(`/api/client/options`);
  if (optionsResponse.status !== 200) {
    throw data("Failed to load the options", {
      status: optionsResponse.status,
    });
  }
  const { data: optionsData } = (await optionsResponse.json()) as {
    data: Array<{
      key: string;
    }>;
  };

  const keys = optionsData?.map((option: { key: string }) => option.key);

  return {
    options: keys,
  };
}

const iconMap = {
  simulation: BarChart2,
  user: Users,
};

function Options() {
  const { t } = useTranslation("options");
  const { options } = useLoaderData<typeof loader>();
  const { user } = use(UserContext);

  return (
    <PageWrapper>
      <div className="mx-xl my-lg min-h-screen">
        <Typography step="3" as="h1" weight="bold" className="mb-sm">
          {t("options:title")}
        </Typography>
        <Card>
          <Tabs
            defaultValue={OPTIONS_VIEWS.byOptions}
            tabs={[
              {
                value: OPTIONS_VIEWS.byOptions,
                label: t("options:byOptions.title"),
                content: (
                  <ul className="gap-md grid grid-cols-1 md:grid-cols-2">
                    {options.map((optionKey: string) => {
                      if (
                        optionKey === "simulation" &&
                        user?.company?.subscriptionType === "premium"
                      ) {
                        return null;
                      }
                      const icon = iconMap[optionKey as keyof typeof iconMap];

                      return (
                        <li key={optionKey}>
                          <OptionItem
                            optionKey={optionKey}
                            title={t(`options:optionsList.${optionKey}.title`)}
                            description={t(
                              `options:optionsList.${optionKey}.description`
                            )}
                            href={"/options/subscription"}
                            tag={t(`options:optionsList.${optionKey}.tag`)}
                            Icon={icon}
                          />
                        </li>
                      );
                    })}
                  </ul>
                ),
              },
              {
                value: OPTIONS_VIEWS.byProServices,
                label: t("options:byProServices.title"),
                content: <ProServicesContent />,
              },
            ]}
          />
        </Card>
      </div>
    </PageWrapper>
  );
}

export default Options;
